// Page: entry-il-found  (Illinois "project found" sign-up)
//
// Ported from the inline <script> in
//   Enrollment_modules/entry-il-found/offer-available.module/module.html (lines 243–2387).
//
// MECHANICAL / FAITHFUL PORT — runtime behaviour is preserved. The only
// deliberate changes, all documented in docs/handoff/global-js-deep-refactor-notes.md:
//   1. Selectors that were scoped to the (HubSpot render-time) wrapper id
//      `#Page_0_Illinois_Project_Found_widget` are now scoped to `root` (the
//      element carrying data-ea-module). Page-global selectors (#date, body,
//      button, overlays) stay document-scoped, as in the original.
//   2. `_hcms/api/*` fetches and same-origin redirects are ROOT-RELATIVE
//      (via core/api `apiUrl`) instead of hardcoding https://enrollment.nautilussolar.com.
//   3. HubDB read URLs are built with core/hubdb `hubdbRowsUrl` (table id +
//      portal from window.__ENV__) instead of inline literals.
//   4. `validatePass` is imported from ui/validation (was a leaked global).
//
// Dynamic ES5 patterns (vars reassigned across types, HubDB row shapes) are typed
// `any` to keep the port mechanical; tightening these is a deep-refactor task.
//
// External globals (crypt, ire, clearDatabase, Sentry) are declared ambiently
// in the theme-root global.d.ts.  dealOwner and first_visited_url were implicit
// window globals in the original but are now recovered as locals inside init().

import { apiUrl } from "../../core/api"
import { hubdbRowsUrl } from "../../core/hubdb"
import * as storage from "../../core/storage"
import { beginOperation, type LoadingOperationHandle } from "../../ui/loading-modal"
import { validatePass } from "../../ui/validation"

// config is unused: the original inline script read everything from localStorage
// and window.__ENV__ (zero HubL tokens), so there are no HubL values to pass in.
export function init(root: HTMLElement, _config: unknown): void {
__ea_trace("modules/entry/entry-il-found.ts","init",[root,_config],["root","_config"]);
  // ─── Scoped DOM helpers ──────────────────────────────────────────────────────
  const q = (sel: string) => root.querySelector(sel) as HTMLElement
  const qi = (sel: string) => root.querySelector(sel) as HTMLInputElement
  const qa = (sel: string) => root.querySelectorAll(sel)
  const di = (id: string) => document.getElementById(id) as HTMLElement
  const dii = (id: string) => document.getElementById(id) as HTMLInputElement
  const fieldVal = (sel: string): string => String($(sel).val() ?? "")

  // `first_visited_url` and `dealOwner` were implicit window globals in the
  // original sloppy-mode inline scripts, set by entry-form's <script> which ran
  // on the same page.  In the module architecture each init() has its own
  // closure; recover the values from localStorage (dealOwner, written by
  // entry-form when it resolves broker attribution) and current href.
  const first_visited_url: string = window.location.href
  const dealOwner: any = storage.rawGet("dealOwner") ?? 0

  // ─── Shared state (was module-scoped in the inline script) ────────────────────
  let isMobile: unknown // never assigned in the original → stays falsy (preserved)
  let secondcheckbox: any = false
  let responseFromAPI: any = ""

  let _utilityIdSession: any
  let _LmiAvailability: any
  let _LmiCensusAvailability: any
  const _DisplayLmiElementList: any[] = []
  const _requireLmiElementList: any[] = []
  const DisplayUsageElementList: any[] = []
  const requireUsageElementList: any[] = []
  const DisplayConsentElementList: any[] = []
  const requireConsentElementList: any[] = []
  let consetPaymentReq: any
  let _LMICensusTractLevels: any
  let sampleSessiontype: any
  let sampleOriginType: any

  // ─── Loading modal ─────────────────────────────────────────────────────────
  // Mechanical migration of the former fake-timer progress bar to the shared,
  // accessible loading modal (js/src/ui/loading-modal.ts). Kind "mutation" so a
  // genuine hang shows honest "may still be processing" copy with a Close escape,
  // never a fabricated 100%. The former 18s/36s message swaps and the
  // interval-leak are gone by construction. A deferred audit will split this into
  // read vs mutation withLoader boundaries with abort/idempotency semantics.
  let loaderHandle: LoadingOperationHandle | null = null
  function openProgressBarIl(message = "We are validating your email"): void {
__ea_trace("modules/entry/entry-il-found.ts","openProgressBarIl",[message],["message"]);
    loaderHandle = beginOperation({ kind: "mutation", message })
  }
  function closeProgressBarIl(): void {
__ea_trace("modules/entry/entry-il-found.ts","closeProgressBarIl",[],[]);
    loaderHandle?.complete()
    loaderHandle = null
  }

  // ─── Loader ─────────────────────────────────────────────────────────────────
  function openLoaderIl(): void {
__ea_trace("modules/entry/entry-il-found.ts","openLoaderIl",[],[]);
    const loader = di("pageFoundLoaderIl")
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("PFloaderOverlayIl").style.display = "block"
    loader.style.display = "flex"
    buttons.forEach((button) => {
__ea_trace("modules/entry/entry-il-found.ts","(anon)",[button],["button"]);
      button.disabled = true
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/entry/entry-il-found.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as unknown as HTMLButtonElement).disabled = true
    })
  }

  function closeLoaderIl(): void {
__ea_trace("modules/entry/entry-il-found.ts","closeLoaderIl",[],[]);
    const loader = di("pageFoundLoaderIl")
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("PFloaderOverlayIl").style.display = "none"
    loader.style.display = "none"
    buttons.forEach((button) => {
__ea_trace("modules/entry/entry-il-found.ts","(anon)",[button],["button"]);
      button.disabled = false
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/entry/entry-il-found.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as unknown as HTMLButtonElement).disabled = false
    })
  }

  // ─── Field validation ─────────────────────────────────────────────────────────
  function validateDate(): void {
__ea_trace("modules/entry/entry-il-found.ts","validateDate",[],[]);
    q("#allfield_error_messageIl").innerText = ""
    const birthdateInput = qi("#date")
    const birthdate = birthdateInput.value.trim()
    if (birthdate === "") {
      q("#date_error_message").innerHTML = "Please enter your Date of Birth."
    } else {
      q("#date_error_message").innerHTML = ""
      const today = new Date()
      const birthdateDate = new Date(birthdate)
      const age = today.getFullYear() - birthdateDate.getFullYear()
      if (age < 18) {
        q("#date_error_message").innerHTML = "You must be 18 years of age or older to enroll."
      } else {
        q("#date_error_message").innerHTML = ""
      }
    }
  }

  function validateDateMobile(): void {
__ea_trace("modules/entry/entry-il-found.ts","validateDateMobile",[],[]);
    q("#allfield_error_messageIl").innerText = ""
    const dateInput = dii("date")
    const inputValue = dateInput.value.trim()
    let sanitizedValue = inputValue.replace(/[^\d]/g, "")
    if (sanitizedValue.length > 2) {
      sanitizedValue = `${sanitizedValue.slice(0, 2)}/${sanitizedValue.slice(2)}`
    }
    if (sanitizedValue.length > 5) {
      sanitizedValue = `${sanitizedValue.slice(0, 5)}/${sanitizedValue.slice(5, 9)}`
    }
    dateInput.value = sanitizedValue
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/
    if (inputValue === "") {
      q("#date_error_message").innerHTML = "Please enter your Date of Birth."
    } else if (!dateRegex.test(sanitizedValue)) {
      q("#date_error_message").innerHTML = "Please enter a date in the format mm/dd/yyyy"
    } else {
      q("#date_error_message").innerHTML = ""
      const birthdateInput = dii("date")
      const birthdate = birthdateInput.value.trim()
      const today = new Date()
      const birthdateDate = new Date(birthdate)
      const age = today.getFullYear() - birthdateDate.getFullYear()
      if (age < 18) {
        q("#date_error_message").innerHTML = "You must be 18 years of age or older to enroll."
      } else {
        q("#date_error_message").innerHTML = ""
      }
    }
  }

  function validateFirstNameIl(): void {
__ea_trace("modules/entry/entry-il-found.ts","validateFirstNameIl",[],[]);
    q("#allfield_error_messageIl").innerText = ""
    const firstName = qi("#first_name").value.trim()
    if (firstName === "") {
      q("#first_name_error_message").innerHTML = "Please enter your First Name."
    } else if (!/^[a-zA-Z ]+$/.test(firstName)) {
      q("#first_name_error_message").innerHTML = "Please enter a valid First Name with only alphabetic characters."
    } else {
      q("#first_name_error_message").innerHTML = ""
    }
  }

  function validateLastNameIl(): void {
__ea_trace("modules/entry/entry-il-found.ts","validateLastNameIl",[],[]);
    q("#allfield_error_messageIl").innerText = ""
    const lastName = qi("#last_name").value.trim()
    if (lastName === "") {
      q("#last_name_error_message").innerHTML = "Please enter your Last Name."
    } else if (!/^[a-zA-Z ]+$/.test(lastName)) {
      q("#last_name_error_message").innerHTML = "Please enter a valid Last Name with only alphabetic characters."
    } else {
      q("#last_name_error_message").innerHTML = ""
    }
  }

  function validateHomePhoneIl(): void {
__ea_trace("modules/entry/entry-il-found.ts","validateHomePhoneIl",[],[]);
    q("#allfield_error_messageIl").innerText = ""
    let phoneNumber = qi("#home_phone").value.trim()
    phoneNumber = phoneNumber.replace(/\D/g, "")
    let CellphoneNumber = qi("#cell_phone").value.trim()
    CellphoneNumber = CellphoneNumber.replace(/\D/g, "")
    if (CellphoneNumber === "" || !/^\d{10}$/.test(CellphoneNumber)) {
      q("#cell_phone_error_message").innerHTML = ""
      if (phoneNumber === "") {
        q("#home_phone_error_message").innerHTML = "Please enter at least one of Mobile or Home Phone."
      } else if (!/^\d{10}$/.test(phoneNumber)) {
        q("#home_phone_error_message").innerHTML = "Please enter a valid 10-digit phone number."
      } else {
        q("#home_phone_error_message").innerHTML = ""
      }
    } else {
      if (phoneNumber === "") {
        q("#home_phone_error_message").innerHTML = ""
      } else if (!/^\d{10}$/.test(phoneNumber)) {
        q("#home_phone_error_message").innerHTML = "Please enter a valid 10-digit phone number."
      } else {
        // (original left the success branch commented out)
      }
    }

    if (phoneNumber.length <= 3) {
      qi("#home_phone").value = phoneNumber
    } else if (phoneNumber.length <= 6) {
      qi("#home_phone").value = phoneNumber.replace(/(\d{3})(\d{1,3})/, "$1-$2")
    } else {
      qi("#home_phone").value = phoneNumber.replace(/(\d{3})(\d{3})(\d{1,4})/, "$1-$2-$3")
    }
  }

  function validateCellPhoneIl(): void {
__ea_trace("modules/entry/entry-il-found.ts","validateCellPhoneIl",[],[]);
    q("#allfield_error_messageIl").innerText = ""
    let phoneNumber = qi("#cell_phone").value.trim()
    phoneNumber = phoneNumber.replace(/\D/g, "")
    let homephoneNumber = qi("#home_phone").value.trim()
    homephoneNumber = homephoneNumber.replace(/\D/g, "")
    if (homephoneNumber === "" || !/^\d{10}$/.test(homephoneNumber)) {
      q("#home_phone_error_message").innerHTML = ""
      if (phoneNumber === "") {
        q("#cell_phone_error_message").innerHTML = "Please enter at least one of Mobile or Home Phone."
      } else if (!/^\d{10}$/.test(phoneNumber)) {
        q("#cell_phone_error_message").innerHTML = "Please enter a valid 10-digit phone number."
      } else {
        q("#cell_phone_error_message").innerHTML = ""
      }
    } else {
      if (phoneNumber === "") {
        q("#cell_phone_error_message").innerHTML = ""
      } else if (!/^\d{10}$/.test(phoneNumber)) {
        q("#cell_phone_error_message").innerHTML = "Please enter a valid 10-digit phone number."
      } else {
        q("#cell_phone_error_message").innerHTML = ""
      }
    }

    if (phoneNumber.length <= 3) {
      qi("#cell_phone").value = phoneNumber
    } else if (phoneNumber.length <= 6) {
      qi("#cell_phone").value = phoneNumber.replace(/(\d{3})(\d{1,3})/, "$1-$2")
    } else {
      qi("#cell_phone").value = phoneNumber.replace(/(\d{3})(\d{3})(\d{1,4})/, "$1-$2-$3")
    }
  }

  function validateEmailIl(): void {
__ea_trace("modules/entry/entry-il-found.ts","validateEmailIl",[],[]);
    q("#allfield_error_messageIl").innerHTML = ""
    const userExistMessage = q("#userAlreadyExistIl")
    userExistMessage.style.display = "none"
    const email = qi("#emailIl").value.trim()
    if (email === "") {
      q("#emailIl_error_messageIl").innerHTML = "Please enter a valid email address."
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      q("#emailIl_error_messageIl").innerHTML = "Please enter a valid email address."
    } else {
      q("#emailIl_error_messageIl").innerHTML = ""
    }
  }

  function validatePasswordIl(): void {
__ea_trace("modules/entry/entry-il-found.ts","validatePasswordIl",[],[]);
    q("#allfield_error_messageIl").innerHTML = ""
    const userExistMessage = q("#userAlreadyExistIl")
    userExistMessage.style.display = "none"
    const password = qi("#passwordIl").value
    const validPassword = validatePass(password)
    if (validPassword.valid) {
      q("#passwordIl_error_message").innerHTML = ""
    } else {
      q("#passwordIl_error_message").innerHTML = validPassword.errors[0]
    }
  }

  function validateFirstCheckbox(): void {
__ea_trace("modules/entry/entry-il-found.ts","validateFirstCheckbox",[],[]);
    const checkbox = qi("#AuthrizationCheckbox")
    if (!checkbox.checked) {
      q("#AuthrizationCheckbox_error_message").innerHTML = "Please check the box to agree to the terms and conditions."
    } else {
      q("#AuthrizationCheckbox_error_message").innerHTML = ""
    }
  }

  function validateSoftCheckbox(): void {
__ea_trace("modules/entry/entry-il-found.ts","validateSoftCheckbox",[],[]);
    const checkbox = qi("#softCheck")
    if (!checkbox.checked) {
      q("#softCheck_error_message").innerHTML = "Please check to agree to the Credit Check."
    } else {
      q("#softCheck_error_message").innerHTML = ""
    }
  }

  function validateFormIl(): boolean {
__ea_trace("modules/entry/entry-il-found.ts","validateFormIl",[],[]);
    const userExistMessage = q("#userAlreadyExistIl")
    secondcheckbox = qi("#eligibleForLMIInput")
    if (secondcheckbox.checked) {
      storage.set("isLMI", "Yes")
    } else {
      storage.set("isLMI", "No")
    }
    userExistMessage.style.display = "none"
    validateFirstNameIl()
    validateLastNameIl()
    validateDate()
    validateHomePhoneIl()
    validateCellPhoneIl()
    validateEmailIl()
    validatePasswordIl()
    validateFirstCheckbox()

    const errorMessages = qa(".error-message")
    let hasErrors = false
    for (let i = 0; i < errorMessages.length; i++) {
      if (errorMessages[i].innerHTML !== "") {
        hasErrors = true
        break
      }
    }

    if (hasErrors) {
      q("#allfield_error_messageIl").innerHTML =
        "There are errors in the form above. Please scroll up and correct them to continue."
      return false
    } else {
      q("#allfield_error_messageIl").innerHTML = ""
      getContactByEmailIl()
    }
    return true
  }

  async function convertDateFormat(dateValue: string): Promise<string> {
__ea_trace("modules/entry/entry-il-found.ts","convertDateFormat",[dateValue],["dateValue"]);
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/
    if (dateRegex.test(dateValue)) {
      const parts = dateValue.split("/")
      const mm = parts[0]
      const dd = parts[1]
      const yyyy = parts[2]
      return `${yyyy}-${mm}-${dd}`
    } else {
      return dateValue
    }
  }

  async function delayIl(ms: number): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","delayIl",[ms],["ms"]);
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
  void delayIl

  // ─── Contact / deal flow ──────────────────────────────────────────────────────
  async function getContactByEmailIl(): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","getContactByEmailIl",[],[]);
    const userExistMessage = q("#userAlreadyExistIl")
    userExistMessage.style.display = "none"
    let email = fieldVal("#project_found-formIl #emailIl")
    email = email.toLowerCase()
    const searchStringInEmail = "@test-nse.com"
    let idTestNseEmail: boolean
    if (email.includes(searchStringInEmail)) {
      idTestNseEmail = true
    } else {
      idTestNseEmail = false
    }
    try {
      openProgressBarIl()
      const data = { email: email }
      const response = await fetch(apiUrl("GetContactRecordBasedOnEmail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      const dataa = await response.json()
      if (dataa.message === "No User Found") {
        if (idTestNseEmail === true) {
          await AddEmailPassInContactIl()
        } else {
          await kickboxCheckEmailIl(email)
        }
      } else if (dataa.message === "User Found") {
        const contactId2 = dataa.userValue.id
        const getPassword = dataa.userValue.properties.password
        await GetAssosiatedDealRecordIl(contactId2, getPassword)
      }
    } catch (error) {
      closeProgressBarIl()
      console.error("Error:", error)
    }
  }

  async function updateDataToContactRecordIl(): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","updateDataToContactRecordIl",[],[]);
    let email = fieldVal("#project_found-formIl #emailIl")
    email = email.toLowerCase()
    let password = fieldVal("#project_found-formIl #passwordIl")
    password = crypt.encrypt(password)
    const first_name = fieldVal("#project_found-formIl #first_name")
    const last_name = fieldVal("#project_found-formIl #last_name")
    const birthdateInput = document.querySelector("#project_found-formIl #date") as HTMLInputElement
    let birthdate = birthdateInput.value.trim()
    if (isMobile) {
      birthdate = await convertDateFormat(birthdate)
    }
    const cell_phone = fieldVal("#project_found-formIl #cell_phone")
    const home_phone = fieldVal("#project_found-formIl #home_phone")
    const u_id = storage.get("utility_id")
    const signUserDevice = storage.get("User Device")
    const signBrowserName = storage.get("Browser Name")
    const signOprSys = storage.get("Operating System")
    let upd_attcode: any = storage.get("attcode")
    let upd_repid: any = storage.get("repid")
    let upd_referralUrl: any = storage.get("referralUrl")
    if (upd_attcode == null) {
      upd_attcode = ""
      upd_repid = ""
      upd_referralUrl = first_visited_url
    } else {
      upd_referralUrl = first_visited_url
    }
    storage.set("LoggedName", `${first_name} ${last_name}`)
    storage.set("CompanyName", `${first_name}&${last_name}`)
    storage.set("FirstName", first_name)
    storage.set("LastName", last_name)
    storage.set("LastName", last_name)

    if (home_phone != null && home_phone !== undefined) {
      storage.set("HomePhone", home_phone)
    }
    if (cell_phone != null && cell_phone !== undefined) {
      storage.set("CellPhone", cell_phone)
    }
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          email: email,
          password: password,
          firstname: first_name,
          lastname: last_name,
          phone: home_phone,
          mobilephone: cell_phone,
          date_of_birth: birthdate,
          first_visited_url: first_visited_url,
          internal_contact_source: "enrollment website",
          enrollment_utility_id: u_id,
          user_device: signUserDevice,
          user_agent: signBrowserName,
          user_agent___operating_system: signOprSys,
          attribution_code: upd_attcode,
          attribution_rep: upd_repid,
          referral_url: upd_referralUrl,
        },
      }
      const response = await fetch(apiUrl("UpdateContactRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        await getContactIdByEmailIl()
      }
      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      closeProgressBarIl()
      console.error("Error:", error)
    }
  }

  async function GetAssosiatedDealRecordIl(contactId2: any, getPassword: any): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","GetAssosiatedDealRecordIl",[contactId2,getPassword],["contactId2","getPassword"]);
    void getPassword
    let catchdata: any = ""
    try {
      const data = { contactId: contactId2 }
      const response = await fetch(apiUrl("FetchAssociatedDealsBasedOnContactId"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      const dealRecordLength = dataa.userValue.associations.deals.results.length
      if (dealRecordLength >= 1) {
        closeProgressBarIl()
        const userExistMessage = q("#userAlreadyExistIl")
        userExistMessage.innerHTML =
          "<p>A user with this email address already exists. Please click below to log in</p>"
        userExistMessage.style.display = "block"
      } else {
        await updateDataToContactRecordIl()
      }
      if (response.status === 500 || response.status === 404) {
        await updateDataToContactRecordIl()
      }
    } catch (error) {
      await updateDataToContactRecordIl()
      console.error("Error:", error)
    }
  }

  async function sha1Hash(email: string): Promise<string> {
__ea_trace("modules/entry/entry-il-found.ts","sha1Hash",[email],["email"]);
    const encoder = new TextEncoder()
    const data = encoder.encode(email)
    const hashBuffer = await crypto.subtle.digest("SHA-1", data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")
    return hashHex
  }

  async function AddEmailPassInContactIl(): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","AddEmailPassInContactIl",[],[]);
    let email = fieldVal("#project_found-formIl #emailIl")
    email = email.toLowerCase()
    let password = fieldVal("#project_found-formIl #passwordIl")
    password = crypt.encrypt(password)
    const first_name = fieldVal("#project_found-formIl #first_name")
    const last_name = fieldVal("#project_found-formIl #last_name")
    const birthdateInput = document.querySelector("#project_found-formIl #date") as HTMLInputElement
    let birthdate = birthdateInput.value.trim()
    if (isMobile) {
      birthdate = await convertDateFormat(birthdate)
    }
    const cell_phone = fieldVal("#project_found-formIl #cell_phone")
    const home_phone = fieldVal("#project_found-formIl #home_phone")
    const u_id = storage.get("utility_id")
    const signUserDevice = storage.get("User Device")
    const signBrowserName = storage.get("Browser Name")
    const signOprSys = storage.get("Operating System")
    let upd_attcode: any = storage.get("attcode")
    let upd_repid: any = storage.get("repid")
    let upd_referralUrl: any = storage.get("referralUrl")
    if (upd_attcode == null) {
      upd_attcode = ""
      upd_repid = ""
      upd_referralUrl = first_visited_url
    } else {
      upd_referralUrl = first_visited_url
    }
    storage.set("LoggedName", `${first_name} ${last_name}`)
    storage.set("CompanyName", `${first_name}&${last_name}`)
    storage.set("FirstName", first_name)
    storage.set("LastName", last_name)
    storage.set("LastName", last_name)

    if (home_phone != null && home_phone !== undefined) {
      storage.set("HomePhone", home_phone)
    }
    if (cell_phone != null && cell_phone !== undefined) {
      storage.set("CellPhone", cell_phone)
    }
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          email: email,
          password: password,
          firstname: first_name,
          lastname: last_name,
          phone: home_phone,
          mobilephone: cell_phone,
          date_of_birth: birthdate,
          first_visited_url: first_visited_url,
          internal_contact_source: "enrollment website",
          enrollment_utility_id: u_id,
          user_device: signUserDevice,
          user_agent: signBrowserName,
          user_agent___operating_system: signOprSys,
          attribution_code: upd_attcode,
          attribution_rep: upd_repid,
          referral_url: upd_referralUrl,
        },
      }
      const response = await fetch(apiUrl("AddContactRecordInHubspot"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data

      if (response.status === 200) {
        await getContactIdByEmailIl()
      } else {
        // (original left this branch empty)
      }
      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      closeProgressBarIl()
      console.error("Error:", error)
    }
  }

  async function UpdateContactIdFromRecordIdIl(id: any): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","UpdateContactIdFromRecordIdIl",[id],["id"]);
    let email = fieldVal("#project_found-formIl #emailIl")
    email = email.toLowerCase()
    let catchdata: any = ""
    try {
      const data = { properties: { email: email, contact_id: id } }
      const response = await fetch(apiUrl("UpdateContactRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      closeProgressBarIl()
      console.error("Error:", error)
    }
  }

  async function getContactIdByEmailIl(): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","getContactIdByEmailIl",[],[]);
    let eml = fieldVal("#project_found-formIl #emailIl")
    eml = eml.toLowerCase()
    let catchdata: any = ""
    try {
      const dt = { email: eml }
      const response = await fetch(apiUrl("GetContactRecordBasedOnEmail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dt),
      })
      catchdata = dt
      const dtaa = await response.json()
      responseFromAPI = JSON.stringify(dtaa)
      if (response.status === 200) {
        const contactId = dtaa.userValue.id
        storage.set("contactId", contactId)
        await UpdateContactIdFromRecordIdIl(contactId)
        const sha1email = await sha1Hash(eml)
        storage.set("sha1email", sha1email)
        ire("identify", {
          customerId: contactId,
          customerEmail: sha1email,
          customProfileId: storage.get("userId"),
        })
        await createDealRecordIl(contactId)
      } else {
        // (original left this branch empty)
      }
    } catch (error) {
      closeProgressBarIl()
      console.error("Error:", error)
    }
  }

  async function createDealRecordIl(contactId: any): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","createDealRecordIl",[contactId],["contactId"]);
    let emaill = fieldVal("#project_found-formIl #emailIl")
    emaill = emaill.toLowerCase()
    const cntId = `New Enrollment - ${contactId}`
    let FarmStateAbbreviation: any = storage.get("FarmStateAbbreviation")
    if (FarmStateAbbreviation === "undefined") {
      FarmStateAbbreviation = ""
    }
    const zip_code = storage.get("zip_code")
    const county = storage.get("county")
    let catchdata: any = ""
    let lmiCheckbox: any = qi("#eligibleForLMIInput")
    if (lmiCheckbox.checked) {
      lmiCheckbox = true
    } else {
      lmiCheckbox = false
    }

    let impctValue: any = storage.get("isImpact")
    let clickId: any = storage.get("clickId")
    if (impctValue !== "Yes") {
      impctValue = "No"
    }
    if (clickId === "null" || clickId == null) {
      clickId = ""
    }
    try {
      const dataa = {
        properties: {
          deal_email_test: emaill,
          dealname: cntId,
          dealstage: "984278683",
          service_state_abbreviation: FarmStateAbbreviation,
          postal_code: zip_code,
          county: county,
          account_holder_or_authorized_user: true,
          eligible_for_lmi: lmiCheckbox,
          credit_check_agreement: true,
          impact_referral: impctValue,
          impact_click_id: clickId,
        },
      }
      const response = await fetch(apiUrl("AddDealRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataa),
      })
      catchdata = dataa
      const dealResult = await response.json()
      responseFromAPI = JSON.stringify(dealResult)
      const dealId = dealResult.id
      if (response.status === 200) {
        storage.set("dealId", dealId)
        await dealAssociateWithContactIl(contactId, dealId)
        ire(
          "trackConversion",
          59966,
          {
            orderId: dealId,
            customProfileId: storage.get("userId"),
            customerId: contactId,
            customerEmail: storage.get("sha1email"),
          },
          { verifySiteDefinitionMatch: true },
        )
        clearDatabase()
      } else {
        // (original left this branch empty)
      }
    } catch (error) {
      closeProgressBarIl()
      console.error("Error:", error)
    }
  }

  async function dealAssociateWithContactIl(contactId: any, dealId: any): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","dealAssociateWithContactIl",[contactId,dealId],["contactId","dealId"]);
    let emailll = fieldVal("#project_found-formIl #emailIl")
    emailll = emailll.toLowerCase()
    void emailll
    let catchdata: any = ""
    try {
      const dataaa = {
        inputs: [{ from: { id: contactId }, to: { id: dealId } }],
      }
      const response = await fetch(apiUrl("dealAssociateWithContact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataaa),
      })
      catchdata = dataaa
      if (response.status === 200) {
        await addedConfigIdWithSomeDataSignUpIl(dealOwner, dealId)
      } else {
        // (original left this branch empty)
      }
      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      closeProgressBarIl()
      console.error("Error:", error)
    }
  }

  // ─── HubDB session config ──────────────────────────────────────────────────────
  async function SaveSessionDetailsOnLocalStorageSignUpIl(): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","SaveSessionDetailsOnLocalStorageSignUpIl",[],[]);
    const exampleUtilityId = storage.get("utility_id")
    const hubDBEndpoint = hubdbRowsUrl("utilityAvailability", {
      utility_id__eq: String(exampleUtilityId),
    })
    const response = await fetch(hubDBEndpoint, { method: "GET" })
    const data = await response.json()
    const rows = data.objects
    if (rows.length >= 1) {
      storage.set("Config ID", rows[0].id)
      rows.forEach((element: any) => {
__ea_trace("modules/entry/entry-il-found.ts","(anon)",[element],["element"]);
        if (exampleUtilityId === element.values["1"]) {
          storage.set("SessionDetails", JSON.stringify(element.values))
          storage.set("stateForLMIConfig", element.values["13"].name)
          storage.set("UtilitySessionDetails", JSON.stringify(element.values))
          storage.set("UtilityRate", element.values["78"])
          storage.set("SubSizingFactor", element.values["79"])
          storage.set("standardRate", element.values["80"])
          storage.set("lmiNseRate", element.values["81"])
          storage.set("standardRate", element.values["80"])
          storage.set("lmiNseRate", element.values["81"])
          storage.set("acSystemSize", element.values["82"])
          storage.set("acKwh", element.values["84"])

          if (element.values["24"] !== undefined) {
            storage.set("credentialProvider", element.values["24"].name)
          }
          _utilityIdSession = element.values["1"]
          if (element.values["68"]) {
            _LMICensusTractLevels = element.values["68"].name
          }
          if (element.values["30"] === "1") {
            _LmiAvailability = "Yes"
          } else if (element.values["30"] === "0") {
            _LmiAvailability = "No"
          } else {
            _LmiAvailability = ""
          }
          if (element.values["31"] === "1") {
            _LmiCensusAvailability = "Yes"
          } else if (element.values["31"] === "0") {
            _LmiCensusAvailability = "No"
          } else {
            _LmiCensusAvailability = ""
          }
        }
      })
    }
  }

  async function checkUsageConfigurationVariationIl(): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","checkUsageConfigurationVariationIl",[],[]);
    let usageAvaiablity: any
    let rows: any
    const SessionLocalStorage = await JSON.parse(storage.get("UtilitySessionDetails") ?? "null")
    if (SessionLocalStorage["32"] === 0) {
      usageAvaiablity = "No"
    } else if (SessionLocalStorage["32"] === 1) {
      usageAvaiablity = "Yes"
    } else {
      usageAvaiablity = ""
    }
    void usageAvaiablity
    try {
      const hubDBEndpoint = hubdbRowsUrl("usageConfig", {
        usage_required_for_contract__eq: "Yes",
        origin_type__eq: sampleOriginType,
        session_type__eq: sampleSessiontype,
      })
      const response = await fetch(hubDBEndpoint, { method: "GET" })
      const data = await response.json()
      rows = data.objects

      if (rows.length >= 1) {
        if (rows[0].values["5"].name === "Yes") {
          DisplayUsageElementList.push("UTILITY CREDENTIALS DISPLAY")
        }
        if (rows[0].values["7"].name === "Yes") {
          DisplayUsageElementList.push("CREATE UTILITY ACCOUNT DISPLAY")
        }
        if (rows[0].values["9"].name === "Yes") {
          DisplayUsageElementList.push("SKIP FOR NOW DISPLAY")
        }
        if (rows[0].values["11"].name === "Yes") {
          DisplayUsageElementList.push("UTILITY ACCOUNT NUMBER DISPLAY")
        }
        if (rows[0].values["13"].name === "Yes") {
          DisplayUsageElementList.push("BILL UPLOAD DISPLAY")
        }

        if (rows[0].values["6"].name === "Yes") {
          requireUsageElementList.push("UTILITY CREDENTIALS REQUIRED")
        }
        if (rows[0].values["8"].name === "Yes") {
          requireUsageElementList.push("CREATE UTILITY ACCOUNT REQUIRED")
        }
        if (rows[0].values["10"].name === "Yes") {
          requireUsageElementList.push("SKIP FOR NOW REQUIRED")
        }
        if (rows[0].values["12"].name === "Yes") {
          requireUsageElementList.push("UTILITY ACCOUNT NUMBER REQUIRED")
        }
        if (rows[0].values["14"].name === "Yes") {
          requireUsageElementList.push("BILL UPLOAD REQUIRED")
        }

        storage.rawSet("usageDisplayelementList", JSON.stringify(DisplayUsageElementList))
        storage.rawSet("usagerequireElementList", JSON.stringify(requireUsageElementList))
      } else {
        storage.rawSet("usageDisplayelementList", JSON.stringify(DisplayUsageElementList))
        storage.rawSet("usagerequireElementList", JSON.stringify(requireUsageElementList))
      }
    } catch (error) {
      console.error("Error in Usage hubdb fetch block:", error)
    }
  }

  async function checkConsentConfigurationVariationIl(): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","checkConsentConfigurationVariationIl",[],[]);
    let rows: any
    const SessionLocalStorage = JSON.parse(storage.get("UtilitySessionDetails") ?? "null")
    if (SessionLocalStorage["33"] === 0) {
      consetPaymentReq = "No"
    } else if (SessionLocalStorage["33"] === 1) {
      consetPaymentReq = "Yes"
    } else {
      consetPaymentReq = "No"
    }
    const hubDBEndpoint = hubdbRowsUrl("paymentConfig", {
      payment_required_for_billing__eq: consetPaymentReq,
      origin_type__eq: sampleOriginType,
      session_type__eq: sampleSessiontype,
    })
    try {
      const response = await fetch(hubDBEndpoint, { method: "GET" })
      const data = await response.json()
      rows = data.objects

      if (rows.length >= 1) {
        if (rows[0].values["5"].name === "Yes") {
          DisplayConsentElementList.push("PAYMENT OPTIONS DISPLAY")
        }
        if (rows[0].values["7"].name === "Yes") {
          DisplayConsentElementList.push("AUTHORIZATION CONSENT DISPLAY")
        }
        if (rows[0].values["9"].name === "Yes") {
          DisplayConsentElementList.push("CREDIT CHECK CONSENT DISPLAY")
        }
        if (rows[0].values["11"].name === "Yes") {
          DisplayConsentElementList.push("SUBSCRIPTION SIGNATURE DISPLAY")
        }
        if (rows[0].values["13"].name === "Yes") {
          DisplayConsentElementList.push("SUB ACKNOWLEDGMENT DISPLAY")
        }
        if (rows[0].values["15"].name === "Yes") {
          DisplayConsentElementList.push("DISCLOSURE SIGNATURE DISPLAY")
        }
        if (rows[0].values["17"].name === "Yes") {
          DisplayConsentElementList.push("DISC ACKNOWLEDGMENT DISPLAY")
        }
        if (rows[0].values["19"].name === "Yes") {
          DisplayConsentElementList.push("SUBSCRIPTION SIGNATURE SIGN DISPLAY")
        }
        if (rows[0].values["21"].name === "Yes") {
          DisplayConsentElementList.push("DISCLOSURE SIGNATURE SIGNED DISPLAY")
        }

        if (rows[0].values["6"].name === "Yes") {
          requireConsentElementList.push("PAYMENT OPTIONS REQUIRED")
        }
        if (rows[0].values["8"].name === "Yes") {
          requireConsentElementList.push("AUTHORIZATION CONSENT REQUIRED")
        }
        if (rows[0].values["10"].name === "Yes") {
          requireConsentElementList.push("CREDIT CHECK CONSENT REQUIRED")
        }
        if (rows[0].values["12"].name === "Yes") {
          requireConsentElementList.push("SUBSCRIPTION SIGNATURE REQUIRED")
        }
        if (rows[0].values["14"].name === "Yes") {
          requireConsentElementList.push("SUB ACKNOWLEDGMENT REQUIRED")
        }
        if (rows[0].values["16"].name === "Yes") {
          requireConsentElementList.push("DISCLOSURE SIGNATURE REQUIRED")
        }
        if (rows[0].values["18"].name === "Yes") {
          requireConsentElementList.push("DISC ACKNOWLEDGMENT REQUIRED")
        }
        if (rows[0].values["20"].name === "Yes") {
          requireConsentElementList.push("SUBSCRIPTION SIGNATURE SIGN REQUIRED")
        }
        if (rows[0].values["22"].name === "Yes") {
          requireConsentElementList.push("DISCLOSURE SIGNATURE SIGNED REQUIRED")
        }
        storage.rawSet("consentDisplayelementList", JSON.stringify(DisplayConsentElementList))
        storage.rawSet("consentrequireElementList", JSON.stringify(requireConsentElementList))
      } else {
        storage.rawSet("consentDisplayelementList", JSON.stringify(DisplayConsentElementList))
        storage.rawSet("consentrequireElementList", JSON.stringify(requireConsentElementList))
      }
    } catch (error) {
      console.error("Error in Usage hubdb fetch block:", error)
    }
  }

  async function kickboxCheckEmailIl(email: string): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","kickboxCheckEmailIl",[email],["email"]);
    let catchdata: any = ""
    try {
      const data = { email: email }
      const response = await fetch(apiUrl("GetEmailVerification"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
      if (response.status === 200) {
        if (result.verificationResult.result === "deliverable") {
          const userExistMessage = q("#userAlreadyExistIl")
          userExistMessage.innerHTML = "<p></p>"
          userExistMessage.style.display = "none"
          await AddEmailPassInContactIl()
        } else if (result.verificationResult.result === "risky") {
          const userExistMessage = q("#userAlreadyExistIl")
          userExistMessage.innerHTML = "<p></p>"
          userExistMessage.style.display = "none"
          await AddEmailPassInContactIl()
        } else if (result.verificationResult.result === "undeliverable") {
          const userExistMessage = q("#userAlreadyExistIl")
          userExistMessage.innerHTML = "<p>Please enter valid email.</p>"
          userExistMessage.style.display = "block"
          closeProgressBarIl()
        } else {
          const userExistMessage = q("#userAlreadyExistIl")
          userExistMessage.innerHTML = "<p>Please enter valid email.</p>"
          userExistMessage.style.display = "block"
          closeProgressBarIl()
        }
      }
    } catch (error) {
      console.error("Error:", error)
    }
  }

  function getCurrentDate(): string {
__ea_trace("modules/entry/entry-il-found.ts","getCurrentDate",[],[]);
    const today = new Date()
    const month = String(today.getMonth() + 1).padStart(2, "0")
    const day = String(today.getDate()).padStart(2, "0")
    const year = today.getFullYear()
    return `${month}/${day}/${year}`
  }

  async function updateTokenAndDateInContact(token: any, date: any, tokenLink: any): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","updateTokenAndDateInContact",[token,date,tokenLink],["token","date","tokenLink"]);
    const email = storage.get("userEmail")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          email: email,
          bayou_onboarding_token: token,
          date_stamp: date,
          bayou_onboarding_link: tokenLink,
        },
      }
      const response = await fetch(apiUrl("UpdateContactRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      responseFromAPI = await response.json()
      responseFromAPI = JSON.stringify(responseFromAPI)
    } catch (error) {
      console.error("Error:", error)
    }
  }

  async function BayouCreateToken(): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","BayouCreateToken",[],[]);
    const UtilitySessionDetails = JSON.parse(storage.get("UtilitySessionDetails") ?? "null")
    const contactId = storage.get("contactId")
    const dealId = storage.get("dealId")
    // External Bayou/Hansen endpoint — host comes from window.__ENV__.bayouApiUrl
    // (per-portal, templates/partials/env.html). Prod value is still TODO-verify there.
    const hensonApiUrl = `${window.__ENV__.bayouApiUrl}/bayouCreateCustomer.php`
    const requestBody = {
      utility: UtilitySessionDetails["9"],
      external_id: contactId,
      deal_id: dealId,
    }
    responseFromAPI = JSON.stringify(requestBody)
    let catchdata: any = ""
    try {
      const response = await fetch(hensonApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      })
      catchdata = requestBody
      const responseData = await response.text()
      responseFromAPI = JSON.stringify(responseData)

      if (typeof Sentry !== "undefined" && Sentry.captureMessage) {
        Sentry.captureMessage("API Response", {
          level: "info",
          tags: {
            api_endpoint: "bayouCreateCustomer",
            api_method: "POST",
            api_status: response.status,
            api_ok: response.ok,
          },
          extra: { requestBody: requestBody, responseData: responseData },
        })
      }

      // Log to Nautilus API
      try {
        fetch(apiUrl("LogToNautilus"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            url: hensonApiUrl,
            request_method: "POST",
            request_body: requestBody,
            response: {
              status: response.status,
              ok: response.ok,
              data: responseData,
            },
            metadata: {
              module_name: "We_Offer_Solar_Power_On_Your_Area_HTML",
              api_endpoint: "bayouCreateCustomer",
              timestamp: new Date().toISOString(),
            },
          }),
        }).catch((e) => {
__ea_trace("modules/entry/entry-il-found.ts","(anon)",[e],["e"]);
          console.error("Failed to log to Nautilus:", e.message)
        })
      } catch (e) {
        console.error("Nautilus logging error:", e)
      }

      const onboardingTokenMatch = responseData.match(/"onboarding_token"\s*:\s*"([^"]+)"/)
      const onboardingLinkMatch = responseData.match(/"onboarding_link"\s*:\s*"([^"]+)"/)
      const onboardingLink = onboardingLinkMatch ? onboardingLinkMatch[1] : null
      if (onboardingTokenMatch?.[1]) {
        const onboardingToken = onboardingTokenMatch[1]
        storage.set("onboarding_token", onboardingToken)
        storage.set("dateStamp", getCurrentDate())
        await updateTokenAndDateInContact(onboardingToken, getCurrentDate(), onboardingLink)
        closeProgressBarIl()
        window.location.href = "/il-usage"
      } else {
        console.error("onboarding_token not found in the response")
        closeProgressBarIl()
        window.location.href = "/il-usage"
      }
      if (response.status === 500 || response.status === 404) {
        closeProgressBarIl()
        window.location.href = "/il-usage"
      }
    } catch (error) {
      closeProgressBarIl()
      window.location.href = "/il-usage"
      console.error("Error calling API:", error)
    }
  }

  async function addedConfigIdWithSomeDataSignUpIl(rep: any, dealId: any): Promise<void> {
__ea_trace("modules/entry/entry-il-found.ts","addedConfigIdWithSomeDataSignUpIl",[rep,dealId],["rep","dealId"]);
    setTimeout(async () => {
__ea_trace("modules/entry/entry-il-found.ts","(anon)",[],[]);
      if (rep === 0 || rep === "" || rep == null) {
        await SaveSessionDetailsOnLocalStorageSignUpIl()
        await checkUsageConfigurationVariationIl()
        await checkConsentConfigurationVariationIl()
        let email: any = fieldVal("#project_found-formIl #emailIl")
        email = email.toLowerCase()
        const SessionLocalStorage = JSON.parse(storage.get("SessionDetails") ?? "null")
        const SessionConfigID = storage.get("Config ID")
        const utility_id = storage.get("utility_id")
        let utility_Name = ""
        const FarmName = storage.get("FarmName")
        const FarmID = storage.get("FarmID")
        const UserDevice = storage.get("User Device")
        const BrowserName = storage.get("Browser Name")
        const opratingSystem = storage.get("Operating System")
        let upd_attcode: any = storage.get("attcode")
        let upd_repid: any = storage.get("repid")
        let upd_referralUrl: any = storage.get("referralUrl")
        if (upd_attcode == null) {
          upd_attcode = ""
          upd_repid = ""
          upd_referralUrl = first_visited_url
        }
        let BillOption: any
        if (SessionLocalStorage != null && SessionLocalStorage !== "null") {
          BillOption = SessionLocalStorage["40"]
        }
        let utility_ext_id_1: any = ""
        let utility_ext_id_1_system: any = ""
        let utility_ext_id_2: any = ""
        let utility_ext_id_2_system: any = ""
        let utility_ext_id_3: any = ""
        let utility_ext_id_3_system: any = ""
        if (SessionLocalStorage != null && SessionLocalStorage !== "null") {
          if (SessionLocalStorage["6"]) {
            utility_Name = SessionLocalStorage["6"]
          }
          if (SessionLocalStorage["7"]) {
            utility_ext_id_1 = SessionLocalStorage["7"]
          }
          if (SessionLocalStorage["8"].name) {
            utility_ext_id_1_system = SessionLocalStorage["8"].name
          }
          if (SessionLocalStorage["9"]) {
            utility_ext_id_2 = SessionLocalStorage["9"]
          }
          if (SessionLocalStorage["10"]) {
            utility_ext_id_2_system = SessionLocalStorage["10"].name
          }
          if (SessionLocalStorage["11"]) {
            utility_ext_id_3 = SessionLocalStorage["11"]
          }
          if (SessionLocalStorage["12"]) {
            utility_ext_id_3_system = SessionLocalStorage["12"].name
          }
        }
        if (upd_attcode === "undefined") {
          upd_attcode = ""
        }
        if (upd_repid === "undefined") {
          upd_repid = ""
        }
        if (upd_referralUrl === "undefined") {
          upd_referralUrl = first_visited_url
        }
        const UtilityRate = storage.get("UtilityRate")
        const SubSizingFactor = storage.get("SubSizingFactor")
        const farm_county = storage.get("FarmCounty")
        let catchdata: any = ""
        try {
          const data = {
            properties: {
              deal_id: dealId,
              solar_farm_id: FarmID,
              farm_id: FarmID,
              solar_farm_name: FarmName,
              il_farm_name: FarmName,
              utility_name: utility_Name,
              utility_id: utility_id,
              session_config_id: SessionConfigID,
              bill_option: BillOption,
              ext_id_1: utility_ext_id_1,
              ext_id_1_system: utility_ext_id_1_system,
              ext_id_2: utility_ext_id_2,
              ext_id_2_system: utility_ext_id_2_system,
              ext_id_3: utility_ext_id_3,
              ext_id_3_system: utility_ext_id_3_system,
              user_agent: BrowserName,
              user_device: UserDevice,
              user_agent___operating_system: opratingSystem,
              attribution_code: upd_attcode,
              attribution_rep: upd_repid,
              referral_url: upd_referralUrl,
              hs_exchange_rate: UtilityRate,
              sub_sizing_factor: SubSizingFactor,
              farm_county: farm_county,
            },
          }
          const response = await fetch(apiUrl("UpdateDealRecord"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
          })
          catchdata = data
          storage.set("userEmail", email.toLowerCase())
          if (storage.get("skipUsage") !== null) {
            storage.remove("skipUsage")
          }

          const result = await response.json()
          responseFromAPI = JSON.stringify(result)
          if (response.status === 200) {
            await BayouCreateToken()
          }

        } catch (error) {
          closeProgressBarIl()
        }
        return true
      } else {
        await SaveSessionDetailsOnLocalStorageSignUpIl()
        await checkUsageConfigurationVariationIl()
        await checkConsentConfigurationVariationIl()
        let email: any = fieldVal("#project_found-formIl #emailIl")
        email = email.toLowerCase()
        const SessionLocalStorage = JSON.parse(storage.get("SessionDetails") ?? "null")
        const SessionConfigID = storage.get("Config ID")
        const utility_id = storage.get("utility_id")
        let utility_Name = ""
        const FarmName = storage.get("FarmName")
        const FarmID = storage.get("FarmID")
        const UserDevice = storage.get("User Device")
        const BrowserName = storage.get("Browser Name")
        const opratingSystem = storage.get("Operating System")
        let upd_attcode: any = storage.get("attcode")
        let upd_repid: any = storage.get("repid")
        let upd_referralUrl: any = storage.get("referralUrl")
        if (upd_attcode == null) {
          upd_attcode = ""
          upd_repid = ""
          upd_referralUrl = first_visited_url
        } else {
          upd_referralUrl = first_visited_url
        }
        let BillOption: any
        if (SessionLocalStorage != null && SessionLocalStorage !== "null") {
          if (SessionLocalStorage["40"]) {
            BillOption = SessionLocalStorage["40"]
          }
        }
        let utility_ext_id_1: any = ""
        let utility_ext_id_1_system: any = ""
        let utility_ext_id_2: any = ""
        let utility_ext_id_2_system: any = ""
        let utility_ext_id_3: any = ""
        let utility_ext_id_3_system: any = ""
        if (SessionLocalStorage != null && SessionLocalStorage !== "null") {
          if (SessionLocalStorage["6"]) {
            utility_Name = SessionLocalStorage["6"]
          }
          if (SessionLocalStorage["7"]) {
            utility_ext_id_1 = SessionLocalStorage["7"]
          }
          if (SessionLocalStorage["8"].name) {
            utility_ext_id_1_system = SessionLocalStorage["8"].name
          }
          if (SessionLocalStorage["9"]) {
            utility_ext_id_2 = SessionLocalStorage["9"]
          }
          if (SessionLocalStorage["10"]) {
            utility_ext_id_2_system = SessionLocalStorage["10"].name
          }
          if (SessionLocalStorage["11"]) {
            utility_ext_id_3 = SessionLocalStorage["11"]
          }
          if (SessionLocalStorage["12"]) {
            utility_ext_id_3_system = SessionLocalStorage["12"].name
          }
        }
        if (upd_attcode === "undefined") {
          upd_attcode = ""
        }
        if (upd_repid === "undefined") {
          upd_repid = ""
        }
        if (upd_referralUrl === "undefined") {
          upd_referralUrl = first_visited_url
        } else {
          upd_referralUrl = first_visited_url
        }
        const UtilityRate = storage.get("UtilityRate")
        const SubSizingFactor = storage.get("SubSizingFactor")
        const farm_county = storage.get("FarmCounty")
        let catchdata: any = ""
        try {
          const data = {
            properties: {
              deal_id: dealId,
              solar_farm_id: FarmID,
              farm_id: FarmID,
              solar_farm_name: FarmName,
              il_farm_name: FarmName,
              utility_name: utility_Name,
              utility_id: utility_id,
              session_config_id: SessionConfigID,
              bill_option: BillOption,
              ext_id_1: utility_ext_id_1,
              ext_id_1_system: utility_ext_id_1_system,
              ext_id_2: utility_ext_id_2,
              ext_id_2_system: utility_ext_id_2_system,
              ext_id_3: utility_ext_id_3,
              ext_id_3_system: utility_ext_id_3_system,
              user_agent: BrowserName,
              user_device: UserDevice,
              user_agent___operating_system: opratingSystem,
              hubspot_owner_id: rep,
              attribution_code: upd_attcode,
              attribution_rep: upd_repid,
              referral_url: upd_referralUrl,
              hs_exchange_rate: UtilityRate,
              sub_sizing_factor: SubSizingFactor,
              farm_county: farm_county,
            },
          }
          const response = await fetch(apiUrl("UpdateDealRecord"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
          })
          catchdata = data
          storage.set("userEmail", email.toLowerCase())
          if (storage.get("skipUsage") !== null) {
            storage.remove("skipUsage")
          }

          const result = await response.json()
          responseFromAPI = JSON.stringify(result)
          if (response.status === 200) {
            await BayouCreateToken()
          }
        } catch (error) {
          closeProgressBarIl()
        }
        return true
      }
    }, 10000)
  }

  async function updateDealStageIl(dealId: any): Promise<boolean> {
__ea_trace("modules/entry/entry-il-found.ts","updateDealStageIl",[dealId],["dealId"]);
    let catchdata: any = ""
    try {
      const data = {
        properties: { deal_id: dealId, dealstage: "261676158" },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      console.error("Error:", error)
    }
    return true
  }
  void updateDealStageIl

  function openPopuppfIl(): void {
__ea_trace("modules/entry/entry-il-found.ts","openPopuppfIl",[],[]);
    q(".youtubevideo-video-pf").style.display = "block"
    q(".youtubevideo-thumb-pf").style.display = "none"
    const iframe = q(".youtubevideo-video-pf iframe") as HTMLIFrameElement
    const videoSrc = iframe.src
    if (!videoSrc.includes("autoplay=1")) {
      iframe.src = `${videoSrc + (videoSrc.includes("?") ? "&" : "?")}autoplay=1`
    }
  }
  void openPopuppfIl
  void openLoaderIl
  void closeLoaderIl

  // ─── Wiring (was top-level in the inline script) ──────────────────────────────
  if (storage.get("attcode") != null) {
    sampleOriginType = "Invite"
    sampleSessiontype = "Consumer Completing"
  } else {
    sampleOriginType = "Direct"
    sampleSessiontype = "Consumer Only"
  }

  q("#logInIl").addEventListener("click", () => {
__ea_trace("modules/entry/entry-il-found.ts","(anon)",[],[]);
    window.location.href = "/account"
  })

  qi("#first_name").addEventListener("input", validateFirstNameIl)
  qi("#last_name").addEventListener("input", validateLastNameIl)
  qi("#home_phone").addEventListener("input", validateHomePhoneIl)
  qi("#cell_phone").addEventListener("input", validateCellPhoneIl)
  qi("#emailIl").addEventListener("input", validateEmailIl)
  qi("#passwordIl").addEventListener("input", validatePasswordIl)
  q("#AuthrizationCheckbox").addEventListener("click", validateFirstCheckbox)
  q("#softCheck").addEventListener("click", validateSoftCheckbox)
  q("#plff_submit").addEventListener("click", validateFormIl)

  window.onload = () => {
__ea_trace("modules/entry/entry-il-found.ts","(anon)",[],[]);
    const dateInput = dii("date")
    if (window.innerWidth <= 1024) {
      dateInput.setAttribute("type", "text")
      q("#date").addEventListener("input", validateDateMobile)
    } else {
      dateInput.setAttribute("type", "date")
      dateInput.focus()
      dateInput.blur()
      q("#date").addEventListener("input", validateDate)
    }
  }

  const togglePassworddd = q("#togglePasswordIl")
  const passworddd = qi("#passwordIl")
  togglePassworddd.addEventListener("click", function (this: HTMLElement) {
__ea_trace("modules/entry/entry-il-found.ts","(anon)",[this],["this"]);
    const typee = passworddd.getAttribute("type") === "password" ? "text" : "password"
    passworddd.setAttribute("type", typee)
    this.classList.toggle("fa-eye-slash")
  })
  $("#project_found-formIl i#togglePasswordIl").on("click", function (this: HTMLElement) {
__ea_trace("modules/entry/entry-il-found.ts","(anon)",[this],["this"]);
    $(this).toggleClass("colored")
  })
}
